import type { SessionPlan } from "@repo/hindi-shared";

/**
 * A tiny module-level hand-off for a custom single-purpose {@link SessionPlan}
 * (drills, single-letter practice) — set immediately before `navigate("/session")`
 * and consumed exactly once by `SessionScreen` on mount.
 *
 * The in-app router only carries a pathname (see `router.tsx`), so there is no
 * URL-based channel for structured data. A module-level variable is enough:
 * navigation is synchronous and same-tab, so there is no window between "set"
 * and "consume" where a re-render could observe a stale value, and consuming
 * clears it so a plain back-navigation to `/session` (e.g. browser back then
 * forward) falls through to the normal assembled daily plan instead of
 * silently replaying the last custom drill.
 */
let pendingPlan: SessionPlan | null = null;

/** Stage a custom plan for the next `/session` mount to pick up. */
export function setPendingPlan(plan: SessionPlan): void {
  pendingPlan = plan;
}

/** Consume (read + clear) the staged custom plan, if any. */
export function takePendingPlan(): SessionPlan | null {
  const plan = pendingPlan;
  pendingPlan = null;
  return plan;
}
